import { isEmpty } from 'lodash';
import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import type { IVehicle } from '@makemydeal/dr-platform-types';

import { CustomerCardState, routingConfig } from '@makemydeal/dr-sales-checkin-types';
import { mapToDxgVehicle } from '@makemydeal/dr-shared-ui-utils';
import {
    buyThisVehicle,
    hideGlobalSpinner,
    setCustomerCardStateAction,
    updateDealAction,
    useCustomerSelector,
    useDealerDetailsSelector,
    useChangeVehicleSelector
} from '@makemydeal/dr-sales-checkin-store';

/**
 * Custom hook to manage vehicle selection in the sales check-in process.
 * It handles the logic for choosing a vehicle, checking if a customer is checked in,
 * and managing the state of the vehicle details.
 */
export const useChangeVehicle = () => {
    const dispatch = useDispatch();
    const vehicleDetails = useChangeVehicleSelector();
    const { checkedInCustomer } = useCustomerSelector();
    const dealerDetails = useDealerDetailsSelector();
    const commonOrgId = dealerDetails?.commonOrgId || '';

    // TODO: Refactor this to use a more appropriate code
    const handleVehicleWithoutCustomer = useCallback(() => {
        if (isEmpty(vehicleDetails) || !vehicleDetails) return true;
        if (isEmpty(checkedInCustomer) && !isEmpty(vehicleDetails)) {
            dispatch(setCustomerCardStateAction(CustomerCardState.SEARCH));
            return true;
        }
        return false;
    }, [checkedInCustomer, vehicleDetails, dispatch]);

    /** Look for this better solution */
    useEffect(() => {
        if (!commonOrgId) return;
        dispatch(hideGlobalSpinner());
    }, [commonOrgId, dispatch]);

    useEffect(() => {
        if (handleVehicleWithoutCustomer()) {
            return;
        }
        const selectedDxgVehicle = mapToDxgVehicle(vehicleDetails as IVehicle);

        // Committing a vehicle is the user's decision to move on, so name Vehicle Details as the
        // destination and let it fire once the save lands. Swapping a vehicle while already
        // IN_PROGRESS is not a status transition, so the workflow evaluator would (correctly) stay
        // silent and strand the user on Browse Inventory. Unconditional by design: picking a vehicle
        // in the showroom always belongs on Vehicle Details, and leaving for Sales View is Deal
        // Setup's decision alone — this hook has no business branching on the dealer's edition.
        dispatch(updateDealAction(selectedDxgVehicle, routingConfig.VEHICLE_DETAILS));
        dispatch(buyThisVehicle({} as IVehicle));
    }, [dispatch, vehicleDetails, handleVehicleWithoutCustomer]);

    return {
        vehicleDetails,
        isReady: commonOrgId ? true : false
    };
};
